'use client';

import { useMemo } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { SortOption } from './filter-types';

interface ShopToolbarProps {
  total: number;
  loading: boolean;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  /** Number of active taxonomy filters (drives the mobile button badge). */
  activeFilterCount: number;
  /** Opens the mobile filter sheet. */
  onOpenFilters: () => void;
}

/**
 * Compact, sticky shop toolbar: live result count on the left, sort control on
 * the right, plus a mobile-only "Filters" button (desktop uses the persistent
 * sidebar). Sticks just below the global header once the grid scrolls up.
 */
export function ShopToolbar({
  total,
  loading,
  sortBy,
  onSortChange,
  activeFilterCount,
  onOpenFilters,
}: ShopToolbarProps) {
  const t = useTranslations('shop');

  const sortOptions = useMemo(
    () => [
      { value: 'best-match' as const, label: t('filterBar.sortOptions.bestMatch') },
      { value: 'newest' as const, label: t('filterBar.sortOptions.newest') },
      { value: 'best-sellers' as const, label: t('filterBar.sortOptions.bestSellers') },
      { value: 'price-low' as const, label: t('filterBar.sortOptions.priceLowToHigh') },
      { value: 'price-high' as const, label: t('filterBar.sortOptions.priceHighToLow') },
    ],
    [t],
  );

  return (
    <div className="sticky top-[var(--scroll-offset,64px)] z-30 -mx-4 border-b border-gray-200 bg-white/95 px-4 backdrop-blur-md dark:border-white/10 dark:bg-slate-950/95 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div className="flex min-h-14 items-center justify-between gap-3 py-2.5">
        {/* Result count */}
        <p className="text-sm text-gray-600 dark:text-gray-400" aria-live="polite">
          {loading ? (
            t('shopClient.results.loading')
          ) : (
            <>
              <span className="font-semibold text-gray-900 dark:text-white">
                {total.toLocaleString()}
              </span>{' '}
              {t('shopClient.results.artworksCountLabel', { count: total })}
            </>
          )}
        </p>

        <div className="flex items-center gap-2">
          {/* Sort — native select for accessible, native-feeling control */}
          <div className="relative">
            <label htmlFor="shop-sort" className="sr-only">
              {t('filterBar.sort.label')}
            </label>
            <select
              id="shop-sort"
              value={sortBy}
              onChange={e => onSortChange(e.target.value as SortOption)}
              className="min-h-11 cursor-pointer appearance-none rounded-lg border border-gray-200 bg-gray-50 py-2 pl-3 pr-9 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/40 dark:border-white/10 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
          </div>

          {/* Mobile filters trigger */}
          <button
            type="button"
            onClick={onOpenFilters}
            className="flex min-h-11 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700 lg:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t('filterBar.filters')}
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
